<!--
  Startseite des Kundenbereichs.

  Eine Frage pro Karte: Wie viele Einheiten habe ich? Was läuft gerade? Wann
  kommt jemand? Alles Weitere steht hinter der Navigation — nicht hier.
-->
<script lang="ts">
  import { api } from '../lib/api.ts';
  import { sitzung, euro, datum, zeitpunkt, PAKETNAME } from '../lib/sitzung.svelte.ts';
  import LeistungenKarte from '../lib/LeistungenKarte.svelte';


  interface Ticket {
    id: string;
    nummer: string;
    titel: string;
    status: string;
    eroeffnet_am: string;
  }

  interface Termin {
    id: string;
    beginn: string;
    art: string;
    status: string;
    ort: string | null;
    titel: string | null;
    buddy_name: string | null;
  }

  const STATUSTEXT: Record<string, string> = {
    neu: 'aufgenommen',
    in_bearbeitung: 'wird bearbeitet',
    wartet: 'wartet auf etwas',
    geschlossen: 'erledigt',
    abgebrochen: 'abgebrochen',
  };

  const ART: Record<string, string> = {
    remote: 'per Fernwartung',
    vor_ort: 'bei Ihnen vor Ort',
    telefon: 'telefonisch',
  };

  let tickets = $state<Ticket[]>([]);
  let termine = $state<Termin[]>([]);

  $effect(() => {
    void (async () => {
      try {
        const [t, te] = await Promise.all([
          api.get<{ tickets: Ticket[] }>('/api/portal/tickets'),
          api.get<{ termine: Termin[] }>('/api/portal/termine'),
        ]);
        tickets = t.tickets;
        termine = te.termine;
      } catch {
        /* Die Einzelseiten melden Fehler ausführlich; hier bleibt die Karte leer. */
      }
    })();
  });

  const offeneTickets = $derived(
    tickets.filter((t) => t.status !== 'geschlossen' && t.status !== 'abgebrochen'),
  );
  const naechsterTermin = $derived(
    termine
      .filter((t) => new Date(t.beginn) > new Date() && t.status !== 'abgesagt')
      .sort((a, b) => a.beginn.localeCompare(b.beginn))[0],
  );
  const einheitenpaket = $derived(sitzung.pakete.find((p) => p.typ === 'einheiten'));
  /**
   * Nur für freigeschaltete Mitarbeiter: die kostenfreien Leistungen als
   * Liste. Seit dem 26.08.2026 ist das ein Safe-Paket-Vertrag (dienstleistung
   * mit Modulverknüpfung) — deshalb alle aktiven Leistungen, nicht nur die
   * Alt-Typen.
   */
  const kostenfreieLeistungen = $derived(sitzung.pakete.filter((p) => p.status === 'aktiv'));
</script>

<div class="kopfzeile">
  <h1>Guten Tag{sitzung.konto?.name ? `, ${sitzung.konto.name}` : ''}</h1>
  {#if einheitenpaket}
    <div class="guthaben-mini">
      {#if einheitenpaket.einheiten_monat === null}
        <div class="guthaben-zahl">unbegrenzt</div>
      {:else}
        <div class="guthaben-zahl">
          Einheiten {Number(einheitenpaket.saldo ?? 0).toLocaleString('de-DE')}
        </div>
        {#if einheitenpaket.naechster_verfall && Number(einheitenpaket.naechster_verfall_menge ?? 0) > 0}
          <div class="guthaben-gueltig">gültig bis {datum(einheitenpaket.naechster_verfall)}</div>
        {/if}
      {/if}
    </div>
  {/if}
</div>

{#if sitzung.kunde?.mitarbeiter}
  <!-- Kostenfrei freigeschalteter Mitarbeiter (Migration 032): Preise, Tarif-
       wechsel und Kaufknöpfe wären hier sinnlos — es läuft keine Zahlung. Die
       Karte sagt stattdessen, was freigeschaltet ist. -->
  <div class="karte">
    <h2>Für Sie kostenfrei freigeschaltet</h2>
    <p>
      Als Mitarbeiterin oder Mitarbeiter nutzen Sie unsere Leistungen kostenfrei — es wird nichts
      abgebucht, und Sie müssen nichts buchen.
    </p>
    {#if kostenfreieLeistungen.length > 0}
      <ul class="eintraege">
        {#each kostenfreieLeistungen as paket (paket.id)}
          <li>
            <div class="zeile">
              <strong>{paket.name ?? PAKETNAME[paket.typ] ?? paket.typ}</strong>
              <span class="marke gut">0,00 € im Monat</span>
            </div>
            <div class="leise klein">
              {#if paket.typ === 'einheiten' && paket.einheiten_monat === null}
                unbegrenzte Einheiten
              {/if}
              {#if paket.aktiv_seit} · seit {datum(paket.aktiv_seit)}{/if}
            </div>
          </li>
        {/each}
      </ul>
    {:else}
      <p class="leise">
        Ihre Freischaltung ist vermerkt, es ist aber noch keine Leistung eingerichtet. Melden Sie
        sich einfach im Team.
      </p>
    {/if}
  </div>
{:else}
  <LeistungenKarte />
{/if}

<div class="karte">
  <h2>Ihre Anfragen</h2>
  {#if offeneTickets.length === 0}
    <p class="leise">Zurzeit ist bei uns nichts von Ihnen offen.</p>
  {:else}
    <ul class="eintraege">
      {#each offeneTickets.slice(0, 4) as ticket (ticket.id)}
        <li>
          <div class="zeile">
            <a href="#/anfragen/{ticket.id}"><strong>{ticket.titel}</strong></a>
            <span class="marke">{STATUSTEXT[ticket.status] ?? ticket.status}</span>
          </div>
          <div class="leise klein">
            {ticket.nummer} · aufgenommen am {datum(ticket.eroeffnet_am)}
          </div>
        </li>
      {/each}
    </ul>
  {/if}
  <div class="knopfreihe">
    <button type="button" onclick={() => (location.hash = '/anfragen')}>
      Neue Anfrage stellen
    </button>
  </div>
</div>

<div class="karte">
  <h2>Ihr nächster Termin</h2>
  {#if naechsterTermin}
    <p class="grosszahl klein-genug">{zeitpunkt(naechsterTermin.beginn)}</p>
    <p>
      {ART[naechsterTermin.art] ?? naechsterTermin.art}
      {#if naechsterTermin.buddy_name} · mit {naechsterTermin.buddy_name}{/if}
      {#if naechsterTermin.ort} · {naechsterTermin.ort}{/if}
    </p>
  {:else}
    <p class="leise">Zurzeit ist kein Termin vereinbart.</p>
    <div class="knopfreihe">
      <button type="button" class="sekundaer" onclick={() => (location.hash = '/termine')}>
        Termin vorschlagen
      </button>
    </div>
  {/if}
</div>

<div class="karte">
  <h2>Anleitungen</h2>
  <p class="leise">Schritt für Schritt, ohne Fachwörter — jede auch als Blatt zum Ausdrucken.</p>
  <ul class="eintraege anleitungen">
    <li><a href="https://it-karsten-fischer.de/hilfe/fernwartung-starten/" target="_blank" rel="noopener">Fernwartung starten</a></li>
    <li><a href="https://it-karsten-fischer.de/hilfe/kundenbereich-anfrage-termin/" target="_blank" rel="noopener">Anfrage stellen und Termin wünschen</a></li>
    <li><a href="https://it-karsten-fischer.de/hilfe/kundenbereich-rechnungen/" target="_blank" rel="noopener">Rechnungen und Verträge ansehen</a></li>
    <li><a href="https://it-karsten-fischer.de/hilfe/kundenbereich-daten-passwort/" target="_blank" rel="noopener">Meine Daten und Passwort ändern</a></li>
    <li><a href="https://it-karsten-fischer.de/hilfe/" target="_blank" rel="noopener"><strong>Alle Anleitungen</strong> (auch Passwort-Safe und Akten-Safe)</a></li>
  </ul>
</div>



<style>
  .klein-genug { font-size: 1.6rem; }

  .anleitungen li { padding: 0.35rem 0; }

  .kopfzeile {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .kopfzeile h1 { margin: 0; }

  .guthaben-mini {
    background: var(--flaeche);
    border: 1px solid var(--rand);
    border-radius: var(--radius);
    padding: 0.6rem 1.1rem;
    text-align: right;
    flex-shrink: 0;
  }

  .guthaben-zahl {
    font-size: 1.8rem;
    font-weight: 700;
    color: var(--tb-navy-700);
    line-height: 1.1;
  }

  .guthaben-gueltig {
    font-size: 0.8rem;
    color: var(--text-leise);
    margin-top: 0.2rem;
  }
</style>
